import { getFileNameFromUrl } from '@/common/getFileNameFromUrl';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { useDynamicLocale } from '@/hoslog-intl';
import { GalleryIcon } from '@/icons';
import { Loader2 } from 'lucide-react';
import { useInquiryDetails } from './hooks/useInquiryDetails';

const dataList = [
  {
    title: 'faq.inquiry.inquiryDetailsModel.content.inquiryId',
    path: 'inquiryId',
  },
  {
    title: 'faq.inquiry.inquiryDetailsModel.content.fullName',
    path: 'nickName',
  },
  {
    title: 'faq.inquiry.inquiryDetailsModel.content.emailAddress',
    path: 'emailAddress',
  },
  {
    title: 'faq.inquiry.inquiryDetailsModel.content.phoneNumber',
    path: 'phoneNumber',
  },
  {
    title: 'faq.inquiry.inquiryDetailsModel.content.inquiryType',
    path: 'inquiryType',
  },
  {
    title: 'faq.inquiry.inquiryDetailsModel.content.companyName',
    path: 'companyName',
  },
  {
    title: 'faq.inquiry.inquiryDetailsModel.content.subject',
    path: 'subject',
  },
  {
    title: 'faq.inquiry.inquiryDetailsModel.content.message',
    path: 'message',
  },
  {
    title: 'faq.inquiry.inquiryDetailsModel.content.attachment',
    path: 'attachment',
  },
] as const;

export const InquiryDetails = ({ inquiryId }: { inquiryId: string }) => {
  const { inquiryDetails, loading } = useInquiryDetails({ inquiryId });
  const { t } = useDynamicLocale();
  const { onOpen } = useDialogFormStore();
  const details = inquiryDetails;
  const downloadUrl = details?.attachment?.path;

  const fileName = getFileNameFromUrl(details?.attachment?.path);
  const noImageFound = fileName === 'no-image.jpeg';
  const downloadFromUrl = async (url: string, fileName: string) => {
    if (!url) return;

    try {
      const res = await fetch(url, { mode: 'cors', cache: 'no-cache' });
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(objectUrl);
    } catch {
      // Ignore download failures; no response details should be exposed in the browser console.
    }
  };

  if (loading) {
    return (
      <div className="w-[556px] h-[300px] flex items-center justify-center">
        <Loader2 />
      </div>
    );
  }

  return (
    <div className="space-y-[10px] px-6 pt-4.5 pb-8">
      {dataList.map((data) => (
        <div key={data.title} className="grid grid-cols-[140px_1fr] gap-3 w-full ">
          <div className="text-inquiry-label">{t(data.title)}</div>
          <div className="font-medium text-inquiry-foreground">
            {data.path === 'attachment' ? (
              fileName ? (
                <div className="flex gap-1 items-center">
                  <GalleryIcon className="text-attachment-foreground" />
                  <p className="text-foreground-icon text-body">{fileName}</p>
                  <span className="text-link ml-[19px] text-caption">
                    <u>
                      <button
                        type="button"
                        onClick={() => {
                          onOpen({
                            title: '',
                            okText: '  ',
                            cancelText: '',
                            size: fileName.endsWith('.pdf') ? 'doc' : 'img',
                            onCancel: () => {},
                            modalTitle: '',
                            component: (
                              <div className="flex items-center justify-center !w-full">
                                {fileName.endsWith('.pdf') ? (
                                  <iframe
                                    src={`${downloadUrl}#toolbar=0`}
                                    title={fileName}
                                    className="w-full h-full"
                                    style={{ height: '80vh' }}
                                  />
                                ) : (
                                  <div className="h-[420px] w-[310px]">
                                    <img
                                      src={details?.attachment.path}
                                      alt={details?.attachment?.alt}
                                      className="h-full w-full object-contain"
                                    />
                                  </div>
                                )}
                              </div>
                            ),
                            image: true,
                          });
                        }}
                        className="underline hover:cursor-pointer"
                      >
                        View
                      </button>
                    </u>
                  </span>
                  <span className="text-link ml-1 text-caption">
                    <button
                      className={`cursor-pointer ${noImageFound && 'hover:cursor-not-allowed opacity-60'}`}
                      type="button"
                      onClick={noImageFound ? () => {} : () => void downloadFromUrl(downloadUrl ?? '', `${fileName}`)}
                    >
                      <u>Download</u>
                    </button>
                  </span>
                </div>
              ) : (
                <p>-</p>
              )
            ) : data.path === 'message' ? (
              <div className="max-h-[100px] overflow-y-auto pr-1 break-all [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb">
                {details?.message ? details?.message : '-'}
              </div>
            ) : (
              (details?.[data.path] ?? '-')
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
